import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getLastResults, getDetailResults } from '../../../../apiservices/CasionApi';
import RefResultModal from '../../../casinoComponents/refResultModal';
import { formatRefMatchTime } from '../../../../utils/refMatchTime';

/**
 * Last Result items in the reference markup (rendered inside `.casino-video-last-results`) for the
 * poker cluster (teen8 / poker / poker6): `item(win)` → { cls, text } per result.
 * Click → full-page .loader-overlay while the round detail loads, then the reference result modal
 * with `renderBody(t1)`.
 */
export default function RefLastResults({ apiType, data = [], item, renderBody, fallbackTitle }) {
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults(apiType);
        if (response?.data?.data?.res) {
          setLastResults(response.data.data.res.map((r) => ({ mid: r.mid, win: String(r.win) })));
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };
    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, [apiType]);

  const handleResultClick = async (r) => {
    if (!r?.mid || loading) return;
    setLoading(true);
    try {
      const response = await getDetailResults(apiType, r.mid.toString());
      const t1 = response?.data?.data?.t1 ?? response?.data?.t1;
      if (t1) {
        setDetailResult(t1);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error fetching detail results:', error);
    }
    setLoading(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setDetailResult(null);
  };

  // API results; before they arrive, the feed's lrs ({mid, win}) as a fallback
  const items = lastResults.length > 0
    ? lastResults
    : (Array.isArray(data) ? data : [])
        .filter((r) => r && typeof r === 'object')
        .map((r) => ({ mid: r.mid, win: String(r.win) }));

  return (
    <>
      {items.map((r, index) => {
        const { cls, text } = item(r.win);
        return (
          <span key={r.mid || index} className={cls || undefined} onClick={() => handleResultClick(r)}>
            {text}
          </span>
        );
      })}
      {loading && createPortal(<div className="loader-overlay" />, document.querySelector('.page-content') || document.body)}
      <RefResultModal
        show={showModal && !loading && !!detailResult}
        onHide={handleCloseModal}
        title={`${detailResult?.ename || fallbackTitle} Result`}
        roundId={detailResult?.rid}
        matchTime={formatRefMatchTime(detailResult?.mtime)}
      >
        {detailResult ? renderBody(detailResult) : null}
      </RefResultModal>
    </>
  );
}
